import React, { useState, useEffect, useCallback } from "react";
import { api } from "../context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Ship, Check, XCircle, Loader2, MapPin, Wallet } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const STEPS = [
  { id: "applied", label: "Applied" },
  { id: "under_review", label: "Under Review" },
  { id: "shortlisted", label: "Shortlisted" },
  { id: "offer", label: "Offer" },
];

const Stepper = ({ status }) => {
  if (status === "rejected") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-bold text-red-600">
        <XCircle className="h-3.5 w-3.5" /> Not selected this time
      </span>
    );
  }
  const activeIdx = STEPS.findIndex((s) => s.id === status);
  return (
    <div className="flex items-center" data-testid="application-stepper">
      {STEPS.map((s, i) => {
        const done = i <= activeIdx;
        return (
          <React.Fragment key={s.id}>
            <div className="flex flex-col items-center">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-black ${done ? "bg-[#002855] text-white" : "bg-gray-100 text-gray-400"}`}>
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={`mt-1 whitespace-nowrap text-[10px] font-bold ${done ? "text-[#002855]" : "text-gray-400"}`}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <span className={`mx-1 mb-4 h-0.5 w-6 sm:w-10 ${i < activeIdx ? "bg-[#002855]" : "bg-gray-200"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const ApplicationsTracker = ({ open, onOpenChange }) => {
  const { toast } = useToast();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setApps((await api.get("/applications")).data.applications);
    } catch {
      toast({ title: "Could not load applications", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(load, 15000); // pick up company status changes
    return () => clearInterval(t);
  }, [open, load]);

  const withdraw = async (id) => {
    try {
      await api.delete(`/applications/${id}`);
      setApps((l) => l.filter((a) => a.id !== id));
      toast({ title: "Application withdrawn" });
    } catch {
      toast({ title: "Could not withdraw", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="applications-tracker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ship className="h-5 w-5 text-[#002855]" /> Your Maritime Applications
          </DialogTitle>
          <DialogDescription>Track each application from Applied to Offer, or withdraw.</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {loading && apps.length === 0 && (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#FF4500]" /></div>
          )}
          {!loading && apps.length === 0 && (
            <p className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-400" data-testid="applications-empty">
              No applications yet — hit Apply on any job in your feed.
            </p>
          )}
          {apps.map((a) => (
            <div key={a.id} className="rounded-xl border border-gray-100 bg-gray-50/60 p-4" data-testid={`application-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-gray-900">{a.job_title}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                    {a.company && <span className="font-semibold text-gray-600">{a.company}</span>}
                    {a.rank && <span>· {a.rank}</span>}
                    {a.vessel && <span>· {a.vessel}</span>}
                    {a.salary && <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" />{a.salary}</span>}
                    {a.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{a.location}</span>}
                  </div>
                  <div className="mt-1 text-xs text-gray-400">
                    Applied {new Date(a.applied_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
                <Button variant="ghost" onClick={() => withdraw(a.id)} data-testid={`withdraw-${a.id}`}
                  className="h-7 shrink-0 rounded-full px-3 text-xs font-bold text-gray-400 hover:text-red-600">
                  Withdraw
                </Button>
              </div>
              <div className="mt-4">
                <Stepper status={a.status} />
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationsTracker;
